import { X } from 'lucide-react';
import { useCallback, useEffect, useId, useRef, type KeyboardEvent, type ReactNode } from 'react';

import { cn } from '../lib/cn';

import { Alert } from './alert';

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

export interface DialogProps {
  open: boolean;
  /** Called on Escape, on backdrop click and from the close button. */
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  /** Footer actions, usually a secondary Cancel and a primary or destructive confirm. */
  footer?: ReactNode;
  /** Suppress the backdrop click, for a confirmation the user must answer. */
  dismissOnBackdrop?: boolean;
  /**
   * A mutation is in flight (rules §17.7, ADMIN_OPERATIONS_UI.md §6 item 21).
   *
   * While true the dialog cannot be dismissed by Escape, by the backdrop or by the close
   * button — the three ways a half-finished write loses the answer that explains it. The
   * focus trap is unchanged and nothing inside is disabled: it is the caller's footer
   * button that carries `loading`, because only the caller knows which action is running.
   */
  busy?: boolean;
  className?: string;
  children?: ReactNode;
}

/**
 * A modal dialog (rules §48.7). Hand-rolled rather than `<dialog>.showModal()` because
 * the native element's focus trap and top-layer behaviour still vary between engines,
 * and because a small explicit implementation is testable in jsdom:
 *
 *   - `role="dialog"` + `aria-modal` + `aria-labelledby` / `aria-describedby`
 *   - focus moves into the dialog on open and returns to the opener on close
 *   - Tab and Shift+Tab wrap inside the dialog
 *   - Escape closes, unless `busy` says a write is in flight
 *   - the rest of the page is inert to pointer events behind the backdrop
 *
 * Nothing is rendered while `open` is false, so a closed dialog costs nothing.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  footer,
  dismissOnBackdrop = true,
  busy = false,
  className,
  children,
}: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const id = useId();
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  // Remember who opened us, move focus in, and put it back on close.
  useEffect(() => {
    if (!open) return undefined;

    openerRef.current = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    return () => {
      openerRef.current?.focus?.();
    };
  }, [open]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        /* Swallowed either way: a dialog that lets Escape through while it is working
           would close whatever is behind it instead. */
        event.stopPropagation();
        if (!busy) onClose();
        return;
      }
      if (event.key !== 'Tab') return;

      const panel = panelRef.current;
      if (panel === null) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (focusable.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      const active = document.activeElement;

      if (event.shiftKey && (active === first || active === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [busy, onClose],
  );

  if (!open) return null;

  return (
    /* The backdrop is a convenience only: Escape and the close button are the real
       affordances, so no interactive role is claimed for it. */
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
      onMouseDown={(event) => {
        if (event.target !== event.currentTarget) return;
        if (dismissOnBackdrop && !busy) {
          onClose();
          return;
        }
        /*
         * M13.7 (WCAG 2.1.2 No Keyboard Trap). A click on the backdrop that is NOT a
         * dismissal — `dismissOnBackdrop={false}`, or a write in flight — used to blur
         * whatever was focused and leave `document.body` active, because the backdrop is
         * not focusable. From `<body>` neither Escape nor Tab reaches the handler below,
         * so the only modal the user could not close with the mouse also became the one
         * they could not close with the keyboard. Suppressing the default keeps focus
         * where it was, inside the panel.
         */
        event.preventDefault();
      }}
      onKeyDown={onKeyDown}
      data-testid="dialog-backdrop"
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        aria-busy={busy ? true : undefined}
        tabIndex={-1}
        className={cn(
          'flex max-h-[90vh] w-full max-w-lg flex-col rounded-card border border-neutral-200 bg-surface shadow-dropdown outline-none',
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 px-6 pt-6 max-[480px]:px-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-xl font-bold text-ink">
              {title}
            </h2>
            {description === undefined ? null : (
              <p id={descriptionId} className="mt-1 text-base text-neutral-500">
                {description}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close dialog"
            className="-mt-1 -mr-1 shrink-0 cursor-pointer rounded-input p-2 text-neutral-400-text hover:bg-neutral-50 hover:text-neutral-600 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={18} strokeWidth={2} aria-hidden={true} />
          </button>
        </div>

        {children === undefined ? null : (
          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5 text-base text-ink max-[480px]:px-4">
            {children}
          </div>
        )}

        {footer === undefined ? null : (
          <div className="flex flex-wrap items-center justify-end gap-3 border-t border-neutral-100 px-6 py-4 max-[480px]:px-4">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ DialogError ---- */

export interface DialogErrorProps {
  /**
   * The server's refusal, verbatim. `undefined` renders nothing at all, so a caller can
   * pass a mutation's error straight through without a ternary at the call site.
   */
  children?: ReactNode;
  /** Bold lead-in. Defaults to nothing: the API's sentence is usually the whole message. */
  title?: ReactNode;
  className?: string;
}

/**
 * The refusal slot inside an open dialog (ADMIN_OPERATIONS_UI.md §6 item 21).
 *
 * The convention it belongs to: **a write that fails leaves its dialog open, with the
 * reason where the answer still is.** Closing on the button press and reporting through a
 * toast is right for a save that cannot really fail, and wrong for anything behind a typed
 * reason, a ticket reference or an amount — the toast is gone in four seconds and the
 * typing is gone with the dialog. See `packages/ui/README.md` for the full recipe.
 *
 * It is an `Alert tone="error"`, so it is `role="alert"` and `aria-live="assertive"`:
 * appearing inside a focus trap the reader is already inside, it is announced without
 * moving focus, which would throw away the caret position in the field being corrected.
 */
export function DialogError({ children, title, className }: DialogErrorProps) {
  if (children === undefined || children === null || children === false) return null;
  return (
    <Alert tone="error" title={title} className={className}>
      {children}
    </Alert>
  );
}
